import {
  TRANSFORMATION_PROFILES,
  buildTransformationSettings,
} from './converter-transformation-settings';

export const OPTIMIZATION_PRESET_STORAGE_PREFIX =
  '2dchat.optimization-presets:';

const COMMON_SETTING_FIELDS = Object.freeze([
  'automaticOptimizationsEnabled',
  'heavyGeometryEnabled',
  'maxError',
  'preserveBorders',
  'targetTrianglesPerSquareMeter',
  'smallDetailAction',
  'smallDetailThresholdMode',
  'smallDetailThresholdMeters',
  'targetMaterial',
  'singleSidedMaterials',
  'textureResizeEnabled',
  'ktx2Enabled',
  'maxDimension',
  'physicalSizeCapEnabled',
  'minDimension',
  'pixelsPerMeter',
]);

const OPTIONAL_SETTING_FIELDS = Object.freeze(['batchingEnabled']);
const PRESET_FIELDS = Object.freeze(['name', 'settings']);
const CATALOG_FIELDS = Object.freeze(['presets']);

function createPresetError(code, message) {
  const error = new Error(message);
  error.code = code;
  return error;
}

function isObject(value) {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function hasOwnField(value, field) {
  return Object.prototype.hasOwnProperty.call(value, field);
}

function assertObjectShape({
  allowedFields,
  requiredFields,
  subject,
  value,
}) {
  if (!isObject(value)) {
    throw new Error(`${subject} must be an object.`);
  }

  for (const field of Object.keys(value)) {
    if (!allowedFields.includes(field)) {
      throw new Error(`${subject} contains unknown field "${field}".`);
    }
  }

  for (const field of requiredFields) {
    if (!hasOwnField(value, field) || value[field] === undefined) {
      throw new Error(`${subject} is missing required field "${field}".`);
    }
  }
}

function normalizePresetName(name) {
  if (typeof name !== 'string') {
    throw createPresetError(
      'invalid-name',
      'Optimization preset name must be a string.',
    );
  }

  const normalizedName = name.trim();

  if (normalizedName.length === 0) {
    throw createPresetError(
      'invalid-name',
      'Optimization preset name must not be blank.',
    );
  }

  return normalizedName;
}

function presetNameKey(name) {
  return name.toLowerCase();
}

function validateAllTransformationFields(settings) {
  const profile = TRANSFORMATION_PROFILES.S123_OPTIMIZATION;
  const validationValues = [
    settings,
    {
      ...settings,
      heavyGeometryEnabled: true,
    },
    {
      ...settings,
      smallDetailAction: 'delete',
    },
    {
      ...settings,
      smallDetailAction: 'delete',
      smallDetailThresholdMode: 'manual',
    },
    {
      ...settings,
      textureResizeEnabled: true,
    },
    {
      ...settings,
      physicalSizeCapEnabled: true,
      textureResizeEnabled: true,
    },
  ];

  for (const values of validationValues) {
    buildTransformationSettings({ profile, values });
  }
}

function normalizePresetSettings(settings) {
  const allowedFields = [
    ...COMMON_SETTING_FIELDS,
    ...OPTIONAL_SETTING_FIELDS,
  ];

  assertObjectShape({
    allowedFields,
    requiredFields: COMMON_SETTING_FIELDS,
    subject: 'Optimization preset settings',
    value: settings,
  });
  validateAllTransformationFields(settings);

  const normalizedSettings = {};

  for (const field of COMMON_SETTING_FIELDS) {
    normalizedSettings[field] = settings[field];
  }

  if (hasOwnField(settings, 'batchingEnabled')) {
    normalizedSettings.batchingEnabled = settings.batchingEnabled;
  }

  return normalizedSettings;
}

function normalizePreset(preset) {
  assertObjectShape({
    allowedFields: PRESET_FIELDS,
    requiredFields: PRESET_FIELDS,
    subject: 'Optimization preset',
    value: preset,
  });

  return {
    name: normalizePresetName(preset.name),
    settings: normalizePresetSettings(preset.settings),
  };
}

function normalizeCatalog(catalog) {
  assertObjectShape({
    allowedFields: CATALOG_FIELDS,
    requiredFields: CATALOG_FIELDS,
    subject: 'Optimization preset catalog',
    value: catalog,
  });

  if (!Array.isArray(catalog.presets)) {
    throw new Error('Optimization preset catalog "presets" must be an array.');
  }

  const presets = catalog.presets.map(normalizePreset);
  const names = new Set();

  for (const preset of presets) {
    const nameKey = presetNameKey(preset.name);

    if (names.has(nameKey)) {
      throw new Error(
        `Optimization preset name "${preset.name}" must be unique.`,
      );
    }

    names.add(nameKey);
  }

  return { presets };
}

function getStorageKey(userGuid) {
  if (typeof userGuid !== 'string' || userGuid.trim().length === 0) {
    throw new Error('Authenticated user GUID must be a non-empty string.');
  }

  return `${OPTIMIZATION_PRESET_STORAGE_PREFIX}${encodeURIComponent(userGuid)}`;
}

function extractSettings({ profile, values }) {
  if (!isObject(values)) {
    throw createPresetError(
      'invalid-values',
      'Optimization form values must be an object.',
    );
  }

  buildTransformationSettings({ profile, values: {} });

  const settings = {};

  for (const field of COMMON_SETTING_FIELDS) {
    settings[field] = values[field];
  }

  if (profile === TRANSFORMATION_PROFILES.S123_OPTIMIZATION) {
    settings.batchingEnabled = values.batchingEnabled;
  }

  return normalizePresetSettings(settings);
}

function findPresetIndex(catalog, name) {
  const nameKey = presetNameKey(name);

  return catalog.presets.findIndex(
    (preset) => presetNameKey(preset.name) === nameKey,
  );
}

export function readOptimizationPresetCatalog({ storage, userGuid }) {
  const storedCatalog = storage.getItem(getStorageKey(userGuid));

  if (storedCatalog === null) {
    return {
      catalog: { presets: [] },
      isCorrupted: false,
    };
  }

  try {
    return {
      catalog: parseOptimizationPresetCatalog(storedCatalog),
      isCorrupted: false,
    };
  } catch {
    return {
      catalog: { presets: [] },
      isCorrupted: true,
    };
  }
}

export function writeOptimizationPresetCatalog({
  catalog,
  storage,
  userGuid,
}) {
  const normalizedCatalog = normalizeCatalog(catalog);

  storage.setItem(
    getStorageKey(userGuid),
    JSON.stringify(normalizedCatalog),
  );

  return normalizedCatalog;
}

export function createOptimizationPreset({
  catalog,
  name,
  profile,
  values,
}) {
  const normalizedCatalog = normalizeCatalog(catalog);
  const normalizedName = normalizePresetName(name);

  if (findPresetIndex(normalizedCatalog, normalizedName) !== -1) {
    throw createPresetError(
      'duplicate-name',
      `Optimization preset "${normalizedName}" already exists.`,
    );
  }

  return {
    presets: [
      ...normalizedCatalog.presets,
      {
        name: normalizedName,
        settings: extractSettings({ profile, values }),
      },
    ],
  };
}

export function replaceOptimizationPreset({
  catalog,
  name,
  profile,
  values,
}) {
  const normalizedCatalog = normalizeCatalog(catalog);
  const normalizedName = normalizePresetName(name);
  const presetIndex = findPresetIndex(normalizedCatalog, normalizedName);

  if (presetIndex === -1) {
    throw createPresetError(
      'preset-not-found',
      `Optimization preset "${normalizedName}" was not found.`,
    );
  }

  const currentPreset = normalizedCatalog.presets[presetIndex];
  const nextSettings = extractSettings({ profile, values });

  if (
    profile === TRANSFORMATION_PROFILES.GLB_IMPORT &&
    hasOwnField(currentPreset.settings, 'batchingEnabled')
  ) {
    nextSettings.batchingEnabled = currentPreset.settings.batchingEnabled;
  }

  const presets = [...normalizedCatalog.presets];
  presets[presetIndex] = {
    name: currentPreset.name,
    settings: nextSettings,
  };

  return { presets };
}

export function deleteOptimizationPreset({ catalog, name }) {
  const normalizedCatalog = normalizeCatalog(catalog);
  const normalizedName = normalizePresetName(name);
  const presetIndex = findPresetIndex(normalizedCatalog, normalizedName);

  if (presetIndex === -1) {
    throw createPresetError(
      'preset-not-found',
      `Optimization preset "${normalizedName}" was not found.`,
    );
  }

  return {
    presets: normalizedCatalog.presets.filter(
      (preset, index) => index !== presetIndex,
    ),
  };
}

export function applyOptimizationPreset({ preset, profile, values }) {
  if (!isObject(values)) {
    throw createPresetError(
      'invalid-values',
      'Optimization form values must be an object.',
    );
  }

  const normalizedPreset = normalizePreset(preset);
  buildTransformationSettings({
    profile,
    values: normalizedPreset.settings,
  });

  const appliedSettings = {};

  for (const field of COMMON_SETTING_FIELDS) {
    appliedSettings[field] = normalizedPreset.settings[field];
  }

  if (
    profile === TRANSFORMATION_PROFILES.S123_OPTIMIZATION &&
    hasOwnField(normalizedPreset.settings, 'batchingEnabled')
  ) {
    appliedSettings.batchingEnabled =
      normalizedPreset.settings.batchingEnabled;
  }

  return {
    ...values,
    ...appliedSettings,
  };
}

export function parseOptimizationPresetCatalog(text) {
  if (typeof text !== 'string') {
    throw new Error('Optimization preset catalog JSON must be a string.');
  }

  return normalizeCatalog(JSON.parse(text));
}

export function mergeOptimizationPresetCatalogs({
  currentCatalog,
  importedCatalog,
}) {
  const normalizedCurrentCatalog = normalizeCatalog(currentCatalog);
  const normalizedImportedCatalog = normalizeCatalog(importedCatalog);
  const presets = [...normalizedCurrentCatalog.presets];
  const presetIndexesByName = new Map(
    presets.map((preset, index) => [presetNameKey(preset.name), index]),
  );
  const conflictNames = [];

  for (const importedPreset of normalizedImportedCatalog.presets) {
    const nameKey = presetNameKey(importedPreset.name);
    const currentIndex = presetIndexesByName.get(nameKey);

    if (currentIndex === undefined) {
      presetIndexesByName.set(nameKey, presets.length);
      presets.push(importedPreset);
      continue;
    }

    presets[currentIndex] = importedPreset;
    conflictNames.push(importedPreset.name);
  }

  return {
    catalog: { presets },
    conflictNames,
  };
}

export function downloadOptimizationPresetCatalog({
  catalog,
  documentRef = globalThis.document,
  urlApi = globalThis.URL,
}) {
  const normalizedCatalog = normalizeCatalog(catalog);
  const blob = new Blob(
    [`${JSON.stringify(normalizedCatalog, null, 2)}\n`],
    { type: 'application/json' },
  );
  const url = urlApi.createObjectURL(blob);
  const link = documentRef.createElement('a');

  link.download = 'optimization-presets.json';
  link.href = url;

  try {
    documentRef.body.appendChild(link);
    link.click();
  } finally {
    try {
      if (link.parentNode !== null) {
        link.parentNode.removeChild(link);
      }
    } finally {
      urlApi.revokeObjectURL(url);
    }
  }
}

export function clearLocalStorageExceptOptimizationPresets(storage) {
  const keysToRemove = [];

  for (let index = 0; index < storage.length; index += 1) {
    const key = storage.key(index);

    if (
      typeof key === 'string' &&
      !key.startsWith(OPTIMIZATION_PRESET_STORAGE_PREFIX)
    ) {
      keysToRemove.push(key);
    }
  }

  for (const key of keysToRemove) {
    storage.removeItem(key);
  }
}
